/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: fix accessibility props */
/** biome-ignore-all lint/a11y/useFocusableInteractive: fix accessibility props */
import cx from 'classnames';
import {useCallback, useContext, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigator} from '~/common/FabricInvitation/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {useImageUpload} from '~/common/FabricInvitation/EventCreateDesign/components/PhotoEditor/common/useImageUpload';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectActiveCardEditor,
  selectCountOfUyoImages,
  selectCurrentSection,
  selectFabricArtBackgroundImage,
  selectShouldUseCachedGalleryPhoto,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {
  GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME,
  GALLERY_PHOTO_PREVIEW_MEDIA_ID,
} from '~/common/indexed-db/dexieStores';
import {GCS_PATH_IDS, gcsUploadImage} from '~/common/storage/gcs_storage';
import {dataURLToFile} from '~/common/utils/dataURLToFile';
import {Button} from '~library/atoms/Button';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {AddPhotosLineIcon} from '~library/svg/AddPhotosLineIcon';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {UploadIcon} from '~library/svg/UploadIcon';
import {charcoal, mexicanOpalWebText, pebble} from '~libSass/base/_exports.sass';

import './UploadButton.sass';

// Lazy load heic2any
const importHeic2any = () => import('heic2any').then((module) => module.default);

export const imageFileUpload = async (file, eventId) => {
  if (file.size > 10 ** 7) {
    toastAlert('Please select an image less than 10MB.', {
      className: 'uyo_toast__error',
      title: 'This image is too large',
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} color={mexicanOpalWebText} />,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    throw new Error('Please select an image less than 10MB.');
  }

  const gcsUploadArgs = {
    gcsPathId: GCS_PATH_IDS.anonymous_image,
    params: {
      event_id: eventId,
      image_id: '',
    },
    image: file,
  };

  return gcsUploadImage(gcsUploadArgs);
};

let isImgRendered = false;

export const UploadButton = ({
  section,
  editor,
  className = '',
  styles,
  callback,
  id,
  index = 1,
  dataQaId = index,
  btnText = '',
  btnSize = 'large',
  isInNavBottom = false,
  showFullWidth = false,
  ref,
}) => {
  const {canvas, processing} = useContext(EventCreateContext);
  const fileRef = useRef();
  const dispatch = useDispatch();
  const isLargishAndUp = useIsLargishAndUp();
  const isMobile = !isLargishAndUp;
  const navigate = useNavigator({isMobile, disableAutoOpenEditor: true});
  const {getCachedMediaDataById} = useCachedMedia(GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME);
  const countOfUyoImages = useSelector(selectCountOfUyoImages);
  const backgroundImage = useSelector(selectFabricArtBackgroundImage);
  const shouldUseCachedGalleryPhoto = useSelector(selectShouldUseCachedGalleryPhoto);
  const isMultipleUyo = countOfUyoImages > 1;
  const {uploading, handleImageUpload} = useImageUpload(canvas);
  const media = getCachedMediaDataById(GALLERY_PHOTO_PREVIEW_MEDIA_ID);

  const onClickHandler = useCallback(() => {
    if (isInNavBottom) {
      navigate('card_design');
    } else {
      fileRef.current.click();
    }
  }, [isInNavBottom, navigate]);

  const onChangeImage = async (event) => {
    const files = event.target ? event.target.files : [];
    let [file] = files;

    if (file) {
      try {
        // since canvas can't render heic files, convert to jpeg
        if (file.type === 'image/heic') {
          // Dynamically import heic2any only when needed
          const heic2any = await importHeic2any();
          const blob = await heic2any({blob: file, toType: 'image/jpeg', quality: 0.9});
          file = new File([blob], 'image.jpeg');
        }

        await handleImageUpload(file, {
          id,
          callback,
          resetInputRef: fileRef,
          isMultipleUyo,
          backgroundImage,
        });

        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {
            activeCardEditor: 'image',
            shouldUseCachedGalleryPhoto: false,
          },
        });
      } catch (_e) {
        // Error handling is done in the hook
      }
    }
  };

  const activeCardEditor = useSelector(selectActiveCardEditor);
  const currentSection = useSelector(selectCurrentSection);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    async function uploadCachedImgToCloud() {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {activeCardEditor: 'image'},
      });

      await handleImageUpload(dataURLToFile(media, 'anonymous_image'), {
        id,
        callback,
        resetInputRef: fileRef,
        isMultipleUyo,
        backgroundImage,
      });
    }

    if (media?.length > 0 && shouldUseCachedGalleryPhoto && !isImgRendered) {
      uploadCachedImgToCloud();
      isImgRendered = true;
    }
  }, [media, shouldUseCachedGalleryPhoto]);

  const activeClass = () => {
    if (editor) {
      return editor === activeCardEditor ? 'create_flow__nav_bottom__section--active' : '';
    }
    return currentSection === section ? 'create_flow__nav_bottom__section--active' : '';
  };

  const activeButtonClass = () => {
    if (editor) {
      return editor === activeCardEditor ? 'active' : '';
    }
    return currentSection === section ? 'active' : '';
  };

  if (isInNavBottom) {
    return (
      <div
        className={`create_flow__nav_bottom__section ${activeClass()}`}
        role="button"
        onClick={onClickHandler}
        data-qa-id="nav-bottom-photo-upload"
        ref={ref}
      >
        <input
          ref={fileRef}
          type="file"
          id="uyo-footer-upload-input"
          accept="image/png,image/jpeg"
          onChange={onChangeImage}
          hidden
        />
        <div className="create_flow__nav_bottom__section__button-wrapper">
          <Button
            id="navBottom"
            variant="secondary"
            size="small"
            className={`create_flow__nav_bottom__section__button ${activeButtonClass()}`}
            data-qa-id="add-photos-upload-button"
          >
            <AddPhotosLineIcon color={charcoal} ratio={0.7} />
          </Button>
        </div>
        <p className="create_flow__nav_bottom__section__title">Photo</p>
      </div>
    );
  }

  // Can't use ECL Button component as we (correctly) don't allow the style prop, but is needed here for Canvas positioning
  return (
    <button
      id={`uyoBtn--${index}`}
      className={cx(
        'button secondary',
        btnSize,
        'uyo-upload-button',
        {hasText: !!btnText, wide: showFullWidth},
        className
      )}
      onClick={onClickHandler}
      data-qa-id={`photo-editor_upload-button--${dataQaId}`}
      type="button"
      style={styles}
      ref={ref}
      disabled={processing}
    >
      <input
        ref={fileRef}
        type="file"
        id={`uyo-upload-input--card-${index}`}
        accept="image/png,image/jpeg"
        onChange={onChangeImage}
        hidden
      />
      {uploading ? (
        <span className="evt-spinner inline no-margin" />
      ) : (
        <>
          <UploadIcon
            className="action_button__upload"
            color={processing ? pebble : charcoal}
            ratio={btnSize === 'small' ? 0.375 : 0.5}
          />
          {btnText && (
            <Typography
              className="uyo-upload-button__text"
              variant={btnSize === 'small' ? 'paragraph4' : 'paragraph3'}
              size="medium"
              color={processing ? 'pebble' : 'charcoal'}
            >
              {btnText}
            </Typography>
          )}
        </>
      )}
    </button>
  );
};
